import { useState } from 'react';
import { PageHeader } from '@/components/composites/PageHeader';
import { SectionCard } from '@/components/composites/SectionCard';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { Select } from '@/components/primitives/Select';
import { Checkbox } from '@/components/primitives/Checkbox';
import { Toggle } from '@/components/primitives/Toggle';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/context/AppContext';
import { apiFetch } from '@/lib/api';
import { noop } from '@/lib/noop';
import { USER_PAUSE_HINT } from '@/content/activity-copy';
import { PRIVACY_STATEMENT_TITLE, PRIVACY_STATEMENT_PARAGRAPHS } from '@/content/privacy-statement';
import styles from './UserSettingsPage.module.css';

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'America/New_York' },
  { value: 'America/Chicago', label: 'America/Chicago' },
  { value: 'America/Denver', label: 'America/Denver' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
  { value: 'Europe/London', label: 'Europe/London' },
] as const;

const DATE_FORMAT_OPTIONS = [
  { value: 'MMM D, YYYY', label: 'MMM D, YYYY (Jan 15, 2026)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
] as const;

const HOURS_FORMAT_OPTIONS = [
  { value: 'decimal', label: 'Decimal (6.5h)' },
  { value: 'clock', label: 'Clock (6:30)' },
] as const;

const WEEK_START_OPTIONS = [
  { value: 'Monday', label: 'Monday' },
  { value: 'Sunday', label: 'Sunday' },
  { value: 'Saturday', label: 'Saturday' },
] as const;

interface Session {
  name: string;
  detail: string;
  time: string;
}

const SESSIONS: Session[] = [
  {
    name: 'This device',
    detail: 'Chrome on Windows \u00b7 New York, NY',
    time: 'Active now',
  },
  {
    name: 'MacBook Pro',
    detail: 'Safari on macOS \u00b7 New York, NY',
    time: '2 hours ago',
  },
];

export function UserSettingsPage() {
  const { data: currentUser } = useCurrentUser();
  const { mockupMode, currentOrg, getToken } = useAppContext();
  const [dailySummary, setDailySummary] = useState(true);
  const [weeklySummary, setWeeklySummary] = useState(true);
  const [gapAlerts, setGapAlerts] = useState(false);

  // Activity AI pause toggle — Feature 200.
  // PATCH /api/v1/orgs/:orgId/employees/:employeeId/preferences
  const [activityPipelinePaused, setActivityPipelinePaused] = useState(false);
  const [pauseSaving, setPauseSaving] = useState(false);
  const [pauseError, setPauseError] = useState<string | null>(null);

  async function handleTogglePause(next: boolean) {
    const previous = activityPipelinePaused;
    setActivityPipelinePaused(next);
    if (mockupMode || !currentUser?.id) return;
    setPauseSaving(true);
    setPauseError(null);
    try {
      const token = await getToken();
      await apiFetch(
        `/api/v1/orgs/${currentOrg.id}/employees/${currentUser.id}/preferences`,
        token,
        { method: 'PATCH', body: JSON.stringify({ activity_pipeline_paused: next }) },
      );
    } catch (err) {
      setActivityPipelinePaused(previous);
      setPauseError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setPauseSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="My Account" />

      {/* Profile */}
      <SectionCard title="Profile">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            noop();
          }}
        >
          <div className={styles.twoCol}>
            <TextInput
              label="First Name"
              name="firstName"
              defaultValue={currentUser?.name.split(' ')[0] ?? ''}
            />
            <TextInput
              label="Last Name"
              name="lastName"
              defaultValue={currentUser?.name.split(' ')[1] ?? ''}
            />
          </div>
          <div className={styles.fields}>
            <TextInput
              label="Email"
              name="email"
              type="email"
              defaultValue={currentUser?.email ?? ''}
              disabled
              hint="Managed by your identity provider. Contact your admin to change."
            />
            <Select
              label="Timezone"
              value={currentUser?.timezone ?? ''}
              options={TIMEZONE_OPTIONS}
              onChange={noop}
            />
          </div>
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Notifications */}
      <SectionCard title="Notifications">
        <div className={styles.checkboxList}>
          <Checkbox checked={dailySummary} onChange={setDailySummary} label="Daily summary email" />
          <Checkbox
            checked={weeklySummary}
            onChange={setWeeklySummary}
            label="Weekly digest email"
          />
          <Checkbox checked={gapAlerts} onChange={setGapAlerts} label="Gap detection alerts" />
        </div>
        <div className={styles.sectionActions}>
          <Button variant="primary" onClick={noop}>
            Save Changes
          </Button>
        </div>
      </SectionCard>

      {/* Display */}
      <SectionCard title="Display">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            noop();
          }}
        >
          <div className={styles.fields}>
            <Select
              label="Date Format"
              value={currentUser?.dateFormat ?? ''}
              options={DATE_FORMAT_OPTIONS}
              onChange={noop}
            />
            <Select
              label="Hours Format"
              value="decimal"
              options={HOURS_FORMAT_OPTIONS}
              onChange={noop}
            />
            <Select
              label="Week Starts On"
              value="Monday"
              options={WEEK_START_OPTIONS}
              onChange={noop}
            />
          </div>
          <div className={styles.sectionActions}>
            <Button variant="primary" type="submit">
              Save Changes
            </Button>
          </div>
        </form>
      </SectionCard>

      {/* Active Sessions */}
      <SectionCard title="Active Sessions">
        <table className={styles.sessionsTable}>
          <tbody>
            {SESSIONS.map((s, i) => (
              <tr key={i}>
                <td className={styles.sessionName}>{s.name}</td>
                <td className={styles.sessionDetail}>{s.detail}</td>
                <td className={styles.sessionTime}>{s.time}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </SectionCard>

      {/* Privacy — Feature 200. Static content from privacy-statement.tsx. */}
      <SectionCard title={PRIVACY_STATEMENT_TITLE}>
        {PRIVACY_STATEMENT_PARAGRAPHS.map((p, i) => (
          <p key={i} className={styles.privacyParagraph}>
            {p}
          </p>
        ))}
      </SectionCard>

      {/* Activity AI — Feature 200. Per-user pause toggle. */}
      <SectionCard title="Activity AI">
        <div className={styles.autoInviteRow}>
          <Toggle
            label="Pause activity proposals"
            checked={activityPipelinePaused}
            onChange={(v) => {
              void handleTogglePause(v);
            }}
            disabled={pauseSaving}
          />
        </div>
        <p className={styles.autoInviteHelp}>{USER_PAUSE_HINT}</p>
        {pauseError && <p className={styles.formError}>{pauseError}</p>}
      </SectionCard>
    </>
  );
}
